import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad, { PointGroup } from 'signature_pad';
import trimCanvas from 'trim-canvas';
import { QcsLoadingButton } from './basic/QcsLoadingButton';
import { canvasToBlob } from 'blob-util';
import { Form, FormikProps } from 'formik';
import { FormikDebounce } from './form/FormikDebounce';
import { FormContainer } from './form/FormContainer';
import { Select } from './form/Select';
import { SaveError } from './SaveError';
import { setErrorStateSnacks } from '../../utils/error';
import { SubmitButton } from './form/SubmitButton';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';
import { QcsMenuItem } from './basic/QcsMenuItem';
import { useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { mediaApi } from '../../utils/api';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { useAppSelector } from '../../store';
import { QcsBox } from './basic/QcsBox';
import { checkFileSize } from '../../utils/upload';
import { getLangNameObj } from '../../utils/format';
import { ErrorStateType } from '../../models/common';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { ShowError } from '../ShowError';
import { Resource } from '@api/resources';

export const ControlButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

interface SignUserForm {
  userId?: string;
}

//The pad draws on a transparent background so trimCanvas can crop by alpha - a
//"backgroundColor" would make trimming a no-op. White is painted after trimming instead.
const createSignatureFile = async (signatureCanvas: HTMLCanvasElement): Promise<File> => {
  //Trim a copy, because trimCanvas mutates the canvas it gets.
  const trimmed = document.createElement('canvas');
  trimmed.width = signatureCanvas.width;
  trimmed.height = signatureCanvas.height;
  trimmed.getContext('2d')!.drawImage(signatureCanvas, 0, 0);
  trimCanvas(trimmed);

  const withWhiteBackground = document.createElement('canvas');
  withWhiteBackground.width = trimmed.width;
  withWhiteBackground.height = trimmed.height;
  const ctx = withWhiteBackground.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, trimmed.width, trimmed.height);
  ctx.drawImage(trimmed, 0, 0);

  const image = await canvasToBlob(withWhiteBackground, 'image/png');
  return new File([image], 'sign.png');
};

//Re-centres a signature inside the pad. Points are stored in CSS pixels from the top left corner,
//so left alone they keep the coordinates they were drawn with: a narrower pad just cuts off the
//right-hand side while the gap on the left stays exactly as it was. Pass the new size only for the
//axis that actually changed - re-centring an axis that did not move would make the signature jump
//for no reason. A signature bigger than the pad cannot fit whatever we do; centring then splits
//the overflow evenly instead of dropping all of it on one side.
const centerSignature = (data: PointGroup[], width: number | null, height: number | null): PointGroup[] => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const group of data) {
    for (const point of group.points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (minX > maxX) {
    return data;
  }

  const dx = width === null ? 0 : (width - (minX + maxX)) / 2;
  const dy = height === null ? 0 : (height - (minY + maxY)) / 2;

  if (dx === 0 && dy === 0) {
    return data;
  }

  return data.map((group) => ({
    ...group,
    points: group.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })),
  }));
};

interface Props {
  playground?: boolean;
  userList: ReferenceDto[];
  save: (userId: string, documentId: string) => Promise<void>;
  saveFile?: (userId: string, file: File) => Promise<void>;
  nextButtonLabel?: string;
  nextButtonSkippableLabel?: string;
  hideSelect?: boolean;
  allowSkip?: boolean;
  handleIsEmpty?: (isEmpty: boolean) => void;
  errorResource?: Resource | Resource[];
}

export const SignUser: FC<Props> = ({
  playground,
  userList,
  save,
  saveFile,
  nextButtonLabel,
  nextButtonSkippableLabel,
  hideSelect = false,
  allowSkip = false,
  handleIsEmpty,
  errorResource,
}) => {
  const { t, i18n } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useAppSnackbar();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const [isEmpty, setIsEmpty] = useState(true);
  const [error, setError] = useState<ErrorStateType>();
  const theme = useTheme();
  const formikRef = useRef<FormikProps<SignUserForm> | null>(null);

  useEffect(() => {
    formikRef.current?.resetForm();
  }, [userList]);

  useEffect(() => {
    if (handleIsEmpty) {
      handleIsEmpty(isEmpty);
    }
  }, [isEmpty, handleIsEmpty]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const signaturePad = new SignaturePad(canvas);
    signaturePadRef.current = signaturePad;

    const handleSignEnd = () => setIsEmpty(signaturePad.isEmpty());
    signaturePad.addEventListener('endStroke', handleSignEnd);

    //The canvas buffer must match its CSS size scaled by devicePixelRatio, otherwise strokes are
    //blurry and offset from the pointer. Assigning canvas.width/height always wipes the bitmap -
    //even when the value is unchanged - so only touch it when the target size really differs, and
    //redraw the strokes afterwards. The pad is a fixed 260px tall and only as wide as the layout,
    //so a phone's address bar sliding in or out changes neither: before this guard that resize
    //still erased the signature, sometimes in the middle of a stroke.
    let padWidth = 0;
    let padHeight = 0;
    let padRatio = 0;

    const resizeCanvas = (force = false) => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const nextPadWidth = canvas.offsetWidth;
      const nextPadHeight = canvas.offsetHeight;
      //A hidden pad measures 0x0. Rebuilding it at that size would throw the drawing away and
      //re-centre the stored points around zero, so leave it untouched until it is back on screen.
      if (nextPadWidth === 0 || nextPadHeight === 0) {
        return;
      }

      //Compare the CSS size and the ratio rather than canvas.width/height: the buffer size is
      //truncated to whole pixels, and plenty of phones report a fractional devicePixelRatio
      //(2.625 and friends), so the stored buffer size would never equal the computed one and the
      //pad would be rebuilt on every single resize event.
      if (!force && nextPadWidth === padWidth && nextPadHeight === padHeight && ratio === padRatio) {
        return;
      }

      //toData returns the pad's live array, so snapshot it before clear() swaps in an empty one.
      const data = signaturePad.toData().slice();
      const shifted = centerSignature(data, nextPadWidth === padWidth ? null : nextPadWidth, nextPadHeight === padHeight ? null : nextPadHeight);

      canvas.width = nextPadWidth * ratio;
      canvas.height = nextPadHeight * ratio;
      //Resetting the buffer also resets the context transform, so re-apply the scale.
      canvas.getContext('2d')?.scale(ratio, ratio);
      padWidth = nextPadWidth;
      padHeight = nextPadHeight;
      padRatio = ratio;

      //clear() realigns the pad's internal state with the now-blank buffer; fromData then repaints
      //the strokes and restores isEmpty() along the way.
      signaturePad.clear();
      if (shifted.length > 0) {
        signaturePad.fromData(shifted);
      }
      setIsEmpty(signaturePad.isEmpty());
    };
    const handleResize = () => resizeCanvas();
    resizeCanvas(true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      signaturePad.removeEventListener('endStroke', handleSignEnd);
      signaturePad.off();
      signaturePadRef.current = null;
    };
  }, []);

  const handleClear = () => {
    signaturePadRef.current?.clear();
    setIsEmpty(true);
  };

  const handleSubmit = async (data: SignUserForm) => {
    setError('');

    //The canvas can be blank even when the submit button is enabled (e.g. cleared by a resize
    //after signing) - an empty canvas would be uploaded as a blank 1x1 image.
    //With allowSkip an empty canvas is a valid "without signature" submit.
    if (!allowSkip && (signaturePadRef.current?.isEmpty() ?? true)) {
      setIsEmpty(true);
      enqueueErrorSnackbar(t('tourniquet.signEmpty'));
      return;
    }

    let documentId: string;
    try {
      const file = await createSignatureFile(canvasRef.current!);

      saveFile?.(data.userId!, file);

      if (playground) {
        documentId = '';
      } else {
        checkFileSize(file);
        const res = await mediaApi.uploadMedia(identityCompanyId, true, file);
        documentId = res.data.id!;
      }
    } catch (err) {
      setErrorStateSnacks(err, setError, enqueueErrorSnackbar);
      return;
    }

    await save(data.userId!, documentId);

    if (!saveFile && playground) {
      enqueueSuccessSnackbar(t('tourniquet.signPlayground'));
    }
    handleClear();
  };

  return (
    <FormikDebounce<SignUserForm> initialValues={{ userId: userList[0]?.id }} onSubmit={handleSubmit} enableReinitialize={true} innerRef={formikRef}>
      {({ isSubmitting }) => (
        <Form>
          <FormContainer>
            {!hideSelect && (
              <Select name="userId" label={t('tourniquet.signer')} disabled={userList.length < 2}>
                {userList.map((x) => (
                  <QcsMenuItem key={x.id} value={x.id}>
                    {getLangNameObj(i18n, x)}
                  </QcsMenuItem>
                ))}
              </Select>
            )}

            <QcsBox
              sx={{
                border: `1px solid ${theme.palette.common.black}`,
                height: '260px',
                backgroundColor: theme.palette.common.white,
              }}
            >
              <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
            </QcsBox>

            <SaveError error={error} />
            {errorResource && <ShowError resource={errorResource} />}

            <ControlButtonsContainer>
              <QcsLoadingButton variant="contained" onClick={handleClear} loading={isSubmitting} disabled={isEmpty}>
                {t('tourniquet.signAgain')}
              </QcsLoadingButton>
              <SubmitButton
                text={allowSkip && nextButtonSkippableLabel ? nextButtonSkippableLabel : nextButtonLabel || t('common.completeTraining')}
                disabled={!allowSkip && isEmpty}
              />
            </ControlButtonsContainer>
          </FormContainer>
        </Form>
      )}
    </FormikDebounce>
  );
};
